// "Start encounter only at the right time" (design log 13 Oct, Experiment; prototype `visitGate`): the encounter of a
// booking opens once the patient has arrived. Before that the action is shown disabled with the reason beside it, so
// the clinician sees why, instead of the action being missing. Pure: the popup passes the booking's status and start.
import { formatClockTime } from '@/shared/lib/time-format';
import { practiceNow } from '@/shared/lib/practice-time';

export type GateStatus = 'arrived' | 'checkedIn' | 'fulfilled' | 'confirmed' | 'booked' | 'noShow' | 'cancelled' | 'waitlisted';

export interface EncounterGate {
  /** Whether the encounter can be entered now. */
  canEnter: boolean;
  /** Why it cannot, said beside the disabled action. Absent when it can. */
  why?: string;
}

const DAY_MS = 86_400_000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** A booking's day, said plainly: tomorrow, or the weekday and date ("Wed 14 Oct"). */
function dayName(start: Date, now: Date): string {
  const days = Math.round((startOfDay(start) - startOfDay(now)) / DAY_MS);
  if (days === 1) return 'tomorrow';
  return `on ${start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`;
}

/**
 * `null` when there is nothing to enter (cancelled, no-show, waitlisted: the action stays hidden, as before).
 * Arrived, checked in and fulfilled keep entering the encounter (a finished visit's encounter is its record and its
 * unsigned note). A confirmed or booked slot is shown disabled: today it opens when the patient checks in; on
 * another day it names that day; a past day's booking that was never attended is over.
 */
export function encounterGate(status: GateStatus, start: Date | null, now: Date = practiceNow()): EncounterGate | null {
  if (status === 'arrived' || status === 'checkedIn' || status === 'fulfilled') return { canEnter: true };
  if (status !== 'confirmed' && status !== 'booked') return null;
  if (!start || Number.isNaN(start.getTime())) return { canEnter: false, why: 'Opens when the patient checks in' };
  const days = Math.round((startOfDay(start) - startOfDay(now)) / DAY_MS);
  if (days < 0) return { canEnter: false, why: 'This visit is over' };
  if (days === 0) return { canEnter: false, why: `Opens when the patient checks in (booked for ${formatClockTime(start)})` };
  return { canEnter: false, why: `Opens ${dayName(start, now)}, once the patient checks in` };
}

/** A wire status code (`appointment.status.code`, any case) read as the gate's status, as the appointment popup reads it
 *  (moved here from the popup so the calendar rows read it the same way). Unknown codes read as booked. */
export function gateStatusOf(code?: string): GateStatus {
  const c = (code ?? '').toLowerCase();
  if (c === 'arrived') return 'arrived';
  if (c === 'confirmed') return 'confirmed';
  if (c === 'noshow') return 'noShow';
  if (c === 'cancelled' || c === 'canceled') return 'cancelled';
  if (c === 'waitlisted' || c === 'proposed') return 'waitlisted';
  if (c === 'checkedin') return 'checkedIn';
  if (c === 'fulfilled' || c === 'completed') return 'fulfilled';
  return 'booked';
}

/**
 * Whether the calendar event carries "Open encounter": the visit is under way, i.e. the popup's "Open encounter" is
 * enabled AND the visit is not finished (Antonin, 16 Oct: "finished visits: keep the button?" → "no"). Finished is the
 * gate's `fulfilled` (wire `Fulfilled` or `completed`, `gateStatusOf`). Gate on: `encounterGate(...).canEnter`; gate off
 * (`?x=-visit-gate`): the popup's own status rule, which the gate's open set equals. So: arrived and checked in (which
 * the app shows as "In Session") only; cancelled, no-show and waitlisted never. The popup's "Open encounter" is not
 * this rule: a finished visit's encounter still opens from there (its record and unsigned note).
 */
export function canOpenEncounter(status: GateStatus, start: Date | null, gateOn: boolean, now: Date = new Date()): boolean {
  if (status === 'fulfilled') return false;
  if (gateOn) return encounterGate(status, start, now)?.canEnter === true;
  return status === 'arrived' || status === 'checkedIn';
}
